import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Flat surface after the reference design: second paper on the page's
 * paper, a hairline ring, no shadow.
 *
 * - `card`     14 px radius, the default for lists, forms and panels
 * - `section`  20 px radius, for large page sections and dialogs
 */
// `dc-card` is the hook for embedding themes (styles/embed-polaris.css).
const cardVariants = cva('dc-card bg-paper-2 text-ink ring-1 ring-line', {
  variants: {
    variant: {
      card: 'rounded-card',
      section: 'rounded-section',
    },
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
    },
  },
  defaultVariants: {
    variant: 'card',
    padding: 'md',
  },
});

export interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, padding }), className)}
      {...props}
    />
  )
);
Card.displayName = 'Card';

/** Title row of a card: title on the left, optional actions on the right. */
const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'mb-4 flex flex-wrap items-center justify-between gap-3',
      className
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('dc-h3 text-base text-ink', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

export { Card, CardHeader, CardTitle, cardVariants };
